<template>
  <div v-if="canRead">
    <div class="card col-md-12">
      <div class="card-header" style="margin-bottom: 10px">
        <h4 class="card-title" style="display:inline">
          Legal Subdivision Search
        </h4>
        <button type="button" v-if="canEdit" @click="addLegalSubdivision" class="btn btn-fill btn-info btn-add-legal-subdivision pull-right">
          Add Legal Subdivision
        </button>
      </div>
      <div class="card-body form-group row">
        <div class="col-md-11">
          <label>&nbsp;</label>
          <el-input type="search" name="search" placeholder="Search" v-validate="'required'" clearable @keyup.enter.native="search" v-model="query" />
          <small class="text-danger" v-show="errors.has('search')">
            {{ getError('search') }}
          </small>
        </div>
        <div class="col-md-1" style="padding-left: 0;">
          <label>Active</label>
          <p-switch v-model="active" type="primary" on-text="YES" off-text="NO" style="display:block"></p-switch>
        </div>
        <div class="col-md-12">
          <button type="button" @click="search" :disabled="isSearchButtonDisabled" class="btn btn-fill btn-info pull-right btn-search">
            Search
          </button>
          <button type="button" @click="clear" class="btn btn-fill btn-info pull-right btn-search">
            Clear
          </button>
        </div>
      </div>
    </div><!-- end card -->
    <div class="card">
      <div class="card-body">
        <div class="col-md-12">
          <el-table class="wrap-whole-words indexed-table-column-width"
                    :data="tableData"
                    style="width: 100%"
                    :max-height="tableHeight(350)">
            <el-table-column
              :width="this.constants.INDEX_COLUMN_WIDTHS.WIDE"
              align="center"
              label="#"
              type="index" />
            <el-table-column label="Legal Subdivision">
              <template slot-scope="props">
                <span class="invisible">{{ props.row.id }}</span>
                <a href="#" @click.prevent="editLegalSubdivision(props.row)">{{ props.row.name }}</a>
              </template>
            </el-table-column>
            <el-table-column
              width="105"
              fixed="right"
              label="Actions">
              <template slot-scope="props">
                <p-button type="success" size="sm" icon>
                  <i class="fa fa-edit" @click="editLegalSubdivision(props.row)"></i>
                </p-button>
                <p-button type="danger" v-if="canEdit" size="sm" icon @click="confirmDelete(props.row)">
                  <i class="fa fa-times"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import {mapGetters} from 'vuex'
  import {notifySuccess, notifyWarning, notifyError, messageForSafeDelete, getTableHeight} from 'src/utilities/utils'
  import _ from 'lodash'
  import Constants from 'src/utilities/constants'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import PButton from 'src/components/UIComponents/Button.vue'
  import Permissions from 'src/utilities/permissions'
  import {OK, NO_CONTENT, UNAUTHORIZED} from 'http-status-codes'

  export default {
    components: {
      PSwitch,
      PButton
    },
    computed: {
      ...mapGetters({
        tableData: 'legalSubdivisions/results'
      }),
      isSearchButtonDisabled() {
        return !this.query
      },
      canRead() {
        return this.$can('read', Permissions.LEGAL_SUBDIVISIONS)
      },
      canEdit() {
        return this.$can('edit', Permissions.LEGAL_SUBDIVISIONS)
      }
    },
    data() {
      return {
        toDelete: null,
        query: null,
        active: true,
        constants: Constants
      }
    },
    methods: {
      clear() {
        this.$validator.pause()
        this.query = null
        this.$store.commit('legalSubdivisions/resetResults')
        setTimeout(this.resumeValidation, 250)
      },
      resumeValidation() {
        this.$validator.resume()
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      editLegalSubdivision(legalSubdivision) {
        this.$eventHub.$emit('show-modal-1', 'Legal Subdivision', {id: legalSubdivision.id}, '500px')
      },
      addLegalSubdivision() {
        this.$eventHub.$emit('show-modal-1', 'Legal Subdivision', null, '500px')
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      async search(optionalLegalSubdivision) {
        let isSearching = true // refreshing search results
        let query = _.trim(this.query)
        if (optionalLegalSubdivision.hasOwnProperty('name')) {
          query = optionalLegalSubdivision.name
          isSearching = false
        }

        // don't fire validation when reloading after adding/editing a street name
        if (!isSearching || (isSearching && await this.$validator.validateAll())) {
          const payload = {
            query: query,
            active: this.active
          }
          const response = await this.$store.dispatch('legalSubdivisions/fetchByName', payload)

          if (OK !== response.status) {
            notifyError(this, 'Error Searching Legal Subdivisions')
          } else if (this.query && response.data.length === 0) {
            this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
          }
        }
      },
      async confirmDelete(legalSubdivision) {
        this.toDelete = legalSubdivision.id
        let message = ''
        let showButton = false

        const responseProjects = await this.$store.dispatch('projects/fetchExistsByLegalSubdivisionId', legalSubdivision.id)

        if (OK !== responseProjects.status) {
          notifyError(this, 'Error determining if ' + legalSubdivision.name + ' is in use. Unable to delete the subdivision.')
          return
        }

        if (responseProjects.data) {
          message = '<strong>' + legalSubdivision.name + '</strong> is used by at least one project and cannot be deleted. You may inactivate it.'
          showButton = false
          this.showConfirmDelete(legalSubdivision, message, showButton)
          return
        }

        message = 'Are you sure you want to delete <strong>' + legalSubdivision.name + '</strong>? This will completely remove the legal subdivision.'

        const responsePriceRules = await this.$store.dispatch('priceRules/fetchFindByLegalSubdivision', legalSubdivision.name)
        const usedByPriceRules = responsePriceRules.data
        if (OK === responsePriceRules.status && usedByPriceRules.length > 0) {
          message = '<strong>' + legalSubdivision.name + '</strong> is used by the following price rules and cannot be deleted:'
          message += '<p>'
          message = messageForSafeDelete(usedByPriceRules, 'shortId', message)
          message += '</p>'
          message += '<p>If you really want to delete this subdivision, you must first remove associations to it in each of the price rules listed above, then come back here and delete the subdivision.</p>'
          showButton = false
          this.showConfirmDelete(legalSubdivision, message, showButton)
          return
        }

        // happy path - legal subdivision can be deleted
        showButton = true
        this.showConfirmDelete(legalSubdivision, message, showButton)
      },
      showConfirmDelete(legalSubdivision, message, showButton) {
        const payload = {
          message,
          entity: legalSubdivision,
          delete: this.delete,
          showButton,
        }
        this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
      },
      async delete() {
        const response = await this.$store.dispatch('delete',
          {
            endpoint: 'legal-subdivisions',
            id: this.toDelete
          })

        if (NO_CONTENT === response.status) {
          this.$store.commit('legalSubdivisions/delete', this.toDelete)
          notifySuccess(this, 'Legal Subdivision Deleted Successfully')
        } else if (UNAUTHORIZED === response.status) {
          notifyWarning(this, 'You are not authorized to delete Legal Subdivision')
        } else {
          notifyError(this, 'Error Deleting Legal Subdivision')
        }
      }
    },
    mounted () {
      this.$eventHub.$on('legalSubdivisionSaved', this.search)
    }
  }
</script>
<style>
  .btn-add-legal-subdivision {
    margin-left: 15px;
  }
  .btn-search {
    margin-bottom: 15px;
   }
  .results-per-page {
    margin-bottom: 5px;
  }
</style>
